import { useEffect, useRef, useState } from "react";
import type { ControlsParams } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState, useMutate } from "../../../../state/editor/useEditor";
import { CompactCheck, CompactNumber, CompactSlider, Row } from "../../inspector/rows";
import type { InertTag } from "../../inspector/schema";

/** Three ControlsParams fields CameraController defaults, echoes and stores,
 *  but that never reach OrbitControls or the update loop. */
const MAX_ZOOM_SPEED_INERT: InertTag = {
  reason: "Stored, never read. OrbitControls' wheel path never consults maxZoomSpeed \u2014 Zoom Speed above is the live knob.",
};
const AUTO_PUSH_INERT: InertTag = {
  reason: "Stored, never read. No update loop consumes autoPushTarget; the camera target only moves via pan/fit.",
};
const AUTO_PULL_INERT: InertTag = {
  reason: "Stored, never read. No update loop consumes autoPullTarget; the camera target only moves via pan/fit.",
};

/**
 * Plain number box that can express ±Infinity — three's OrbitControls ships
 * maxDistance/maxZoom/minAzimuthAngle/maxAzimuthAngle at ±Infinity by default,
 * which `CompactNumber`'s `<input type="number">` can't display (renders
 * blank/broken). Shows "∞"/"-∞"; clearing the box restores Infinity (with the
 * field's natural sign) so the default is reachable again, not just NaN/0.
 */
function InfNumber({ value, negative, width = 76, onChange }: { value: number; negative?: boolean; width?: number; onChange: (v: number) => void }) {
  const fallbackInf = negative ? -Infinity : Infinity;
  const display = (v: number) => (v === Infinity ? "∞" : v === -Infinity ? "-∞" : String(v));
  const [local, setLocal] = useState(display(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setLocal(display(value)); }, [value]);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={local}
      style={{ width }}
      onFocus={() => { focused.current = true; }}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        focused.current = false;
        const t = local.trim();
        if (t === "" || t === "∞" || t === "-∞" || t === "inf" || t === "-inf") {
          onChange(t === "-∞" || t === "-inf" ? -Infinity : t === "" ? fallbackInf : Infinity);
          return;
        }
        const n = parseFloat(t);
        onChange(Number.isFinite(n) ? n : fallbackInf);
      }}
      className="w-16 rounded-md border border-ed-border bg-white px-1.5 py-1 text-right text-[12px] text-ed-text outline-none focus:border-ed-accent"
    />
  );
}

const DEG_TO_RAD = Math.PI / 180;
const POLAR_HINT = "Radians. The same limit as Camera › \u201cOrbit V Min/Max (\u00b0)\u201d above, which shows it in degrees.";
/** Static unit tag — the parity guard scans `label` strings against the captured
 * reference schema ("minPolarAngle"), so the unit rides beside the input rather
 * than inside the label. */
const RAD_SUFFIX = <span className="shrink-0 text-[11px] text-ed-text-3">rad</span>;

/** Full OrbitControls + camera parameter set (iJewel Camera › Controls). */
export function ControlsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const scene = useDomainState("scene");
  const mutate = useMutate();
  const [p, setP] = useState<ControlsParams | null>(null);
  useEffect(() => { r.getControlsParams().then(setP).catch(() => {}); }, [r]);
  if (!p) return null;
  const set = (patch: Partial<ControlsParams>) => r.setControlsParams(patch).then(setP);
  const num = (v: number, key: keyof ControlsParams) => (
    <CompactNumber value={v} width={76} onChange={(nv) => void set({ [key]: nv } as Partial<ControlsParams>)} />
  );
  const infNum = (v: number, key: keyof ControlsParams, negative?: boolean) => (
    <InfNumber value={v} negative={negative} onChange={(nv) => void set({ [key]: nv } as Partial<ControlsParams>)} />
  );

  /**
   * Polar (vertical orbit) limits are ONE viewer setting with two rows in this
   * same section: the "Orbit V Min/Max (°)" sliders above (bound to
   * `scene.polarLimits`, which `setPolarLimits` treats as DEGREES) and these
   * two radian number inputs, which the reference schema specifies in radians
   * (default 3.14159…). They used to be wired to two different RPCs with two
   * different local mirrors, so the same limit read "180" here and "3.1416"
   * eight rows down, and editing either left the other showing the old value
   * for the rest of the session.
   *
   * Both now go through the single `scene.polarLimits` slice + `setPolarLimits`
   * — these rows just convert deg↔rad at the edge — so the pair can no longer
   * disagree, and the unit each one speaks is spelled out beside it.
   */
  const polar: [number, number] = scene?.polarLimits ?? [0, 180];
  const setPolarDeg = (i: 0 | 1, deg: number) => {
    const next: [number, number] = i === 0 ? [deg, polar[1]] : [polar[0], deg];
    mutate("scene", {
      patch: { polarLimits: next },
      call: (api) => Promise.resolve(api.setPolarLimits(next[0], next[1])),
    });
  };
  const polarRad = (i: 0 | 1, allowInf?: boolean) => {
    const rad = polar[i] * DEG_TO_RAD;
    const onChange = (nv: number) => setPolarDeg(i, nv / DEG_TO_RAD);
    // Max keeps the ∞ affordance: a project/vjson may park maxPolarAngle at
    // Infinity, which `<input type="number">` renders blank.
    return allowInf
      ? <InfNumber value={rad} onChange={onChange} />
      : <CompactNumber value={rad} step={0.01} width={76} onChange={onChange} />;
  };
  return (
    <>
      <Row label="Enabled"><CompactCheck checked={p.enabled} onChange={(v) => void set({ enabled: v })} /></Row>
      <Row label="Dolly Zoom"><CompactCheck checked={p.dollyZoom} onChange={(v) => void set({ dollyZoom: v })} /></Row>
      <Row label="Zoom to Cursor"><CompactCheck checked={p.zoomToCursor} onChange={(v) => void set({ zoomToCursor: v })} /></Row>
      <Row label="Enable Damping"><CompactCheck checked={p.enableDamping} onChange={(v) => void set({ enableDamping: v })} /></Row>
      <Row label="Damping Factor">{num(p.dampingFactor, "dampingFactor")}</Row>
      <Row label="Auto Rotate"><CompactCheck checked={p.autoRotate} onChange={(v) => void set({ autoRotate: v })} /></Row>
      <Row label="Auto Rotate Speed">{num(p.autoRotateSpeed, "autoRotateSpeed")}</Row>
      <Row label="Rotate Speed"><CompactSlider value={p.rotateSpeed} min={0.1} max={4} step={0.1} onChange={(v) => void set({ rotateSpeed: v })} /></Row>
      <Row label="Enable Zoom"><CompactCheck checked={p.enableZoom} onChange={(v) => void set({ enableZoom: v })} /></Row>
      <Row label="Zoom Speed">{num(p.zoomSpeed, "zoomSpeed")}</Row>
      <Row label="Max Zoom Speed" inert={MAX_ZOOM_SPEED_INERT}>{num(p.maxZoomSpeed, "maxZoomSpeed")}</Row>
      <Row label="Enable Pan"><CompactCheck checked={p.enablePan} onChange={(v) => void set({ enablePan: v })} /></Row>
      <Row label="Pan Speed">{num(p.panSpeed, "panSpeed")}</Row>
      <Row label="Auto Push Target" inert={AUTO_PUSH_INERT}><CompactCheck checked={p.autoPushTarget} onChange={(v) => void set({ autoPushTarget: v })} /></Row>
      <Row label="Auto Pull Target" inert={AUTO_PULL_INERT}><CompactCheck checked={p.autoPullTarget} onChange={(v) => void set({ autoPullTarget: v })} /></Row>
      <Row label="Min Distance">{num(p.minDistance, "minDistance")}</Row>
      <Row label="Max Distance">{infNum(p.maxDistance, "maxDistance")}</Row>
      <Row label="Min Zoom">{num(p.minZoom, "minZoom")}</Row>
      <Row label="Max Zoom">{infNum(p.maxZoom, "maxZoom")}</Row>
      <Row label="Min Polar Angle" hint={POLAR_HINT}>{polarRad(0)}{RAD_SUFFIX}</Row>
      <Row label="Max Polar Angle" hint={POLAR_HINT}>{polarRad(1, true)}{RAD_SUFFIX}</Row>
      <Row label="Min Azimuth Angle">{infNum(p.minAzimuthAngle, "minAzimuthAngle", true)}</Row>
      <Row label="Max Azimuth Angle">{infNum(p.maxAzimuthAngle, "maxAzimuthAngle")}</Row>
      <Row label="Auto Near/Far"><CompactCheck checked={p.autoNearFar} onChange={(v) => void set({ autoNearFar: v })} /></Row>
      <Row label="Dolly FoV"><CompactCheck checked={p.dollyFov} onChange={(v) => void set({ dollyFov: v })} /></Row>
      <Row label="Min Near">{num(p.minNear, "minNear")}</Row>
      <Row label="Max Far">{num(p.maxFar, "maxFar")}</Row>
    </>
  );
}
